import { Serializer } from "@vueuse/core";
import { defineStore } from "pinia";
import { RouteLocationNormalizedGeneric } from "vue-router";

type Condition = (to: RouteLocationNormalizedGeneric) => boolean;

type StaticQueryParamOptions<T> = {
	defaultVal?: T;
	condition?: Condition;
	serializer?: Serializer<T>;
};

export function useStaticQueryParam<T>(name: string, opts: StaticQueryParamOptions<T> = {}) {
	const route = useRoute();

	const condition = opts.condition || (() => true);
	const reader = opts.serializer?.read || ((val) => val as T);
	const writer = opts.serializer?.write || ((val) => String(val));

	const rawQueryParam = computed<string | null>(() => {
		let param: string | null = null;
		const queryParam = route.query[name];
		if (Array.isArray(queryParam)) {
			param = (queryParam[0] as string) ?? null;
		} else {
			param = (queryParam as string) ?? null;
		}

		return param;
	});

	const parsedQueryParam = computed<T | null>(() => {
		if (rawQueryParam.value == null) {
			return null;
		}

		return reader(rawQueryParam.value);
	});

	const localVal = ref<T | null>(parsedQueryParam.value);
	watch(parsedQueryParam, (param) => {
		if (param == null && localVal.value == null) {
			return;
		}

		if (param == null || localVal.value == null || writer(param) !== writer(localVal.value)) {
			localVal.value = param;
		}
	});

	/**
	 * Check the opts condition and that the localVal is different than the defaultVal
	 */
	const isEnabled = computed(() => {
		return condition(route) && localVal.value !== (opts.defaultVal || null);
	});

	const manager = useStaticQueryManager();
	watchImmediate(isEnabled, (enabled) => {
		if (!enabled) {
			return;
		}

		// Passed from disabled to enabled, write the current localValue to the url
		if (localVal.value != null) {
			manager.set(name, writer(localVal.value));
		}

		manager.retain(name);
		onWatcherCleanup(() => {
			manager.release(name);
		});
	});

	return computed({
		get() {
			return localVal.value || opts.defaultVal || null;
		},
		set(val) {
			localVal.value = val;

			if (!isEnabled.value) {
				return;
			}

			let paramVal: string | undefined;
			if (val != null) {
				paramVal = writer(val);
			}

			// We are removing the param, we inform the manager to skip this key on next guard update.
			// Without this, the guard will put back the query in the URL.
			if (paramVal == null) {
				manager.skip(name);
			}

			manager.set(name, paramVal);
		},
	}) as WritableComputedRef<T>;
}

export const useStaticQueryManager = defineStore("static-query-manager", () => {
	const router = useRouter();

	const staticKeys = reactive(new Map<string, number>());
	const queue = reactive(new Map<string, string | undefined>());
	const nextIgnore = new Set<string>();

	/**
	 * Tell if the route was changed by the user, using the back or forward button of the browser.
	 * When true, next guard execution will be ignored.
	 */
	let isPushOrPop = false;
	router.options.history.listen((_, __, info) => {
		const { direction } = info;
		isPushOrPop = direction === "forward" || direction === "back";
	});

	/**
	 * This function batch all updates in the same tick to prevent concurrency issues
	 */
	function set(key: string, value: string | undefined) {
		queue.set(key, value);
	}

	/**
	 * Inform the manager that it should retain this key between route navigation
	 */
	function retain(key: string) {
		staticKeys.set(key, (staticKeys.get(key) || 0) + 1);
	}

	/**
	 * Release this key from the manager. This key will not be keep
	 */
	function release(key: string) {
		staticKeys.set(key, (staticKeys.get(key) || 0) - 1);
		if ((staticKeys.get(key) || 0) <= 0) {
			staticKeys.delete(key);
			queue.set(key, undefined);
		}
	}

	function skip(key: string) {
		nextIgnore.add(key);
	}

	watch(
		queue,
		() => {
			if (queue.size === 0) {
				return;
			}

			router.replace({
				...router.currentRoute.value,
				query: {
					...router.currentRoute.value.query,
					...Object.fromEntries(queue.entries()),
				},
			});

			queue.clear();
		},
		{ flush: "post" }
	);

	router.beforeEach((to, from) => {
		if (isPushOrPop) {
			isPushOrPop = false;
			return;
		}

		let navChanged = false;

		const prevQuery = from.query;
		const newQuery = { ...to.query };

		for (const [key, count] of staticKeys) {
			if (count === 0) {
				continue;
			}
			if (nextIgnore.has(key)) {
				continue;
			}

			// Query param got dropped, set it back
			if (prevQuery[key] != null && newQuery[key] == null) {
				newQuery[key] = prevQuery[key];
				navChanged = true;
			}
		}

		nextIgnore.clear();

		if (navChanged) {
			return {
				...to,
				query: newQuery,
			};
		}
	});

	return {
		staticKeys,
		queue,
		set,
		retain,
		release,
		skip,
	};
});
